import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import DefaultLayout from "../../layouts/DefaultLayout";
import { withRouter } from "react-router-dom";
import {
	Button,
	Card,
	Col,
	DatePicker,
	Descriptions,
	Form,
	Input,
	Modal,
	Row,
	Table,
} from "antd";
import { debounce } from "lodash";
import { defaultCurrencyFormat, formRules } from "../../../utilities";
import quoteStatuses from "../../../configs/quote-statuses";
import moment from "moment";
import { CancellationReasonPicker } from "../../ui/forms/NotRenewingReasonPicker";

@inject("context")
@observer
class CancellationModal extends Component {
	componentDidMount() {
		this.doQuoteCancellationCalculation(new Date());
	}

	formRef = (form) => {
		this.form = form;
	};

	render() {
		const service = this.props.context.quotesService;
		const quote = service.cancellationModalRecord;
		const visible = service.cancellationModalVisible;

		return (
			<Modal
				title="Policy cancellation"
				visible={visible}
				onOk={this.handleOk}
				onCancel={this.handleCancel}
			>
				<Descriptions column={1}>
					<Descriptions.Item label="Policy Number">
						{quote.policyNumber}
					</Descriptions.Item>
					<Descriptions.Item label="Policy Holder">
						{quote.first_name} {quote.last_name}
					</Descriptions.Item>
				</Descriptions>
				<Form
					ref={this.formRef}
					layout="vertical"
					initialValues={{ cancellationDate: moment(new Date()) }}
				>
					<Row>
						<Col>
							<Form.Item
								name="cancellationReason"
								label="Cancellation Reason"
								rules={[formRules.required]}
							>
								<CancellationReasonPicker />
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col>
							<Form.Item
								name="cancellationDate"
								label="Cancellation date"
								rules={[formRules.required]}
							>
								<DatePicker onChange={(e) => this.handleDateChange(e)} />
							</Form.Item>
						</Col>
					</Row>
				</Form>
				<Descriptions column={1}>
					<Descriptions.Item label="Gross Premium incl. IPT">
						{defaultCurrencyFormat(quote.total)}
					</Descriptions.Item>
					<Descriptions.Item label="Adjustment">
						{defaultCurrencyFormat(quote.premiumAdjustment)}
					</Descriptions.Item>
					<Descriptions.Item label="Cancellation fee">
						{defaultCurrencyFormat(quote.cancellationFee)}
					</Descriptions.Item>
					<Descriptions.Item label="Total Due/Refund">
						{defaultCurrencyFormat(quote.totalDueRefund)}
					</Descriptions.Item>
				</Descriptions>
			</Modal>
		);
	}

	handleOk = () => {
		const { quotesService, paymentService } = this.props.context;
		if (
			this.form.getFieldValue("cancellationReason") &&
			this.form.getFieldValue("cancellationDate")
		) {
			quotesService.hideCancellationModal();
			const cancelledPolicy = quotesService.cancellationModalRecord;
			const refundIssued = paymentService.refund(
				cancelledPolicy.quoteId,
				cancelledPolicy.totalDueRefund
			);
			if (refundIssued) {
				quotesService.cancelQuote(
					this.form.getFieldValue("cancellationReason"),
					this.form.getFieldValue("cancellationDate").toISOString()
				);
				this.props.onModalOk();
			}
		}
	};

	handleCancel = async () => {
		const service = this.props.context.quotesService;

		service.hideCancellationModal();
		service.setCancellationModalRecord(null);
	};

	handleDateChange = (e) => {
		const cancellationDate = e;
		this.doQuoteCancellationCalculation(cancellationDate.toISOString());
	};
}

/**
 * Shows insurance policies.
 */
@inject("context")
@withRouter
@observer
class Quotes extends Component {
	componentDidMount() {
		this.reloadQuotes();
	}

	render() {
		const service = this.props.context.quotesService;

		const columns = [
			{
				title: "Date Created",
				dataIndex: "createdOn",
				sorter: true,
				defaultSortOrder: "descend",
				render: (date) => {
					return new Date(date).toLocaleDateString();
				},
			},
			{
				title: "Quote number",
				dataIndex: "quoteId",
				sorter: true,
			},
			{
				title: "First name",
				dataIndex: "first_name",
				sorter: true,
				render: (name) => {
					if (name) {
						return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
					}
					return "";
				},
			},
			{
				title: "Last name",
				dataIndex: "last_name",
				sorter: true,
				render: (name) => {
					if (name) {
						return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
					}
					return "";
				},
			},
			{
				title: "Quote",
				dataIndex: "total",
				render: (n) => {
					return defaultCurrencyFormat(n);
				},
			},
			{
				title: "Phone number",
				dataIndex: "phoneNumber",
			},
			{
				title: "Email",
				dataIndex: "organisationEmail",
			},
			{
				title: "Address 1",
				dataIndex: "address1",
			},

			{
				title: "City",
				dataIndex: "city",
			},
			{
				title: "Postcode",
				dataIndex: "postcode",
			},
			{
				title: "Actions",
				render: (field, record) => {
					return (
						<>
							<Button
								type="primary"
								onClick={() => this.handleUpdateButtonClick(record)}
							>
								Update
							</Button>
							<Button
								type="default"
								onClick={() => this.handleViewButtonClick(record)}
							>
								View
							</Button>
						</>
					);
				},
			},
		];

		return (
			<DefaultLayout
				layoutContentProps={{
					style: {
						maxWidth: "100%",
						paddingLeft: 40,
						paddingRight: 40,
					},
				}}
			>
				<Row>
					<Col xs={24}>
						<Card title={<h1>Quotes</h1>}>
							<Row>
								<Col
									xs={6}
									style={{
										paddingBottom: "15px",
									}}
								>
									<Input.Search
										onSearch={this.handleSearchChange}
										onChange={this.handleSearchChange}
										value={service.search.value}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24}>
									<Table
										rowKey="quoteId"
										columns={columns}
										dataSource={service.quotesData}
										pagination={service.pagination}
										loading={service.loading}
										onChange={this.handleTableChange}
									/>
									{service.cancellationModalRecord && (
										<CancellationModal onModalOk={this.reloadQuotes} />
									)}
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
			</DefaultLayout>
		);
	}

	handleTableChange = (pagination, filters, sorter) => {
		const service = this.props.context.quotesService;

		service.setPagination(pagination);
		service.setSorter(sorter);

		service.loadQuotesData();
	};

	handleSearchChange = (...args) => {
		const value =
			args.length === 2
				? args[1].currentTarget.value
				: args[0].currentTarget.value;

		this.props.context.quotesService.setSearch({
			fields: [
				"id",
				"organisation.first_name",
				"organisation.last_name",
				"organisation.post_code",
				"organisation.phone",
			],
			value,
		});

		this.debouncedReloadItems();
	};

	handleUpdateButtonClick = async (record) => {
		const { quoteId } = record;
		const { policiesService } = this.props.context;
		policiesService.setIsViewMode(false);


		this.props.context.insuranceMtaService.setOverThePhone(true);
		this.props.history.push(`/mta/amendquote/${quoteId}`);

	};

	handleViewButtonClick = async (record) => {
		const { quoteId } = record;

		const { policiesService } = this.props.context;
		policiesService.setIsViewMode(true);
		this.props.context.insuranceMtaService.setOverThePhone(true);
		this.props.history.push(`/mta/amendquote/${quoteId}`);

	};


	reloadQuotes = async () => {
		await this.props.context.quotesService.loadQuotesData();
	};

	debouncedReloadItems = debounce(this.reloadQuotes, 250);
}

export default Quotes;
